// src/components/admin/InviteLinksModal.js - Shows freshly issued invite links
// so Admins can share them outside email (corporate filters often block invites).
// Links are only available right after they are issued: the server keeps a
// hash, not the link, so they can't be fetched again later - only regenerated.
import { useState } from 'react';
import PropTypes from 'prop-types';
import { exportToCSV } from '../../utils/csvExport';
import './Admin.css';

export const downloadInviteLinksCSV = (links, filename) => {
  const result = exportToCSV(links, [
    { key: 'email', label: 'Email' },
    { key: 'inviteLink', label: 'Invite Link' }
  ], filename);
  if (!result.success) {
    alert(`Failed to export CSV: ${result.error}`);
  }
};

const InviteLinksModal = ({ title, intro, links, filename, onClose }) => {
  const [copiedEmail, setCopiedEmail] = useState(null);

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.inviteLink);
      setCopiedEmail(link.email);
    } catch (err) {
      // Clipboard API unavailable: the link field is selectable as a fallback
      console.error('Copy failed:', err);
      setCopiedEmail(null);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '720px' }}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button onClick={onClose} className="modal-close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {intro && <p className="text-sm text-gray-600 mb-4">{intro}</p>}

        {links.length === 0 ? (
          <p className="text-sm text-gray-600 mb-4">There are no pending invitations.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Each link works once and expires in 14 days. Copy or download them now: they can&apos;t be shown again,
              only regenerated (which stops the old link working).
            </p>
            <div className="space-y-4 mb-4" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {links.map(link => (
                <div key={link.email}>
                  <div className="text-sm font-semibold text-gray-800 mb-1">{link.email}</div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={link.inviteLink}
                      onFocus={(e) => e.target.select()}
                      className="form-input flex-1"
                      aria-label={`Invite link for ${link.email}`}
                    />
                    <button type="button" onClick={() => handleCopy(link)} className="btn btn-secondary btn-sm">
                      {copiedEmail === link.email ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="modal-footer">
          {links.length > 0 && (
            <button type="button" onClick={() => downloadInviteLinksCSV(links, filename)} className="btn btn-secondary flex-1">
              Download CSV
            </button>
          )}
          <button type="button" onClick={onClose} className="btn btn-primary flex-1">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

InviteLinksModal.propTypes = {
  title: PropTypes.string.isRequired,
  intro: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape({
    email: PropTypes.string.isRequired,
    inviteLink: PropTypes.string.isRequired
  })).isRequired,
  filename: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default InviteLinksModal;
